import { type Dayjs } from "dayjs"

import { DATE_FORMAT } from "../../../../../../../utils/date/date.ts"
import type { ProjectMilestone } from "../../../../../types/project-phases.types.tsx"
import { type MilestoneBulkActionParams } from "./MilestonesBulkActionBar.tsx"

type DatePatch = Pick<ProjectMilestone, "id"> &
  Partial<Pick<ProjectMilestone, "start_date" | "end_date">>

interface Refusal {
  id: string
  message: string
}

/**
 * The PATCH each selected milestone needs for a bulk "Shift Dates" or "Set
 * Dates". A shift moves whichever of the stored Planned Start / Planned End a
 * milestone has by the same number of days; a set writes only the dates the
 * user picked. Neither touches Actual End. A milestone the change cannot apply
 * to — nothing to shift, or a result that ends before it starts — is refused
 * rather than sent.
 */
export function milestoneDatePatches(
  params: MilestoneBulkActionParams,
  milestones: ProjectMilestone[],
  parseDate: (value: unknown) => Dayjs | null,
  messages: { noDatesToShift: string; endBeforeStart: string }
): { patches: DatePatch[]; refused: Refusal[] } {
  const byId = new Map(milestones.map((m) => [m.id, m]))
  const patches: DatePatch[] = []
  const refused: Refusal[] = []

  params.ids.forEach((id) => {
    const milestone = byId.get(id)
    const storedStart = parseDate(milestone?.start_date)
    const storedEnd = parseDate(milestone?.end_date)

    if (params.action === "shift-dates") {
      const days = params.shiftDays ?? 0
      if (!storedStart && !storedEnd) {
        refused.push({ id, message: messages.noDatesToShift })
        return
      }
      patches.push({
        id,
        ...(storedStart
          ? { start_date: storedStart.add(days, "day").format(DATE_FORMAT) }
          : {}),
        ...(storedEnd
          ? { end_date: storedEnd.add(days, "day").format(DATE_FORMAT) }
          : {})
      })
      return
    }

    const nextStart = params.startDate ? parseDate(params.startDate) : storedStart
    const nextEnd = params.endDate ? parseDate(params.endDate) : storedEnd
    if (nextStart && nextEnd && nextEnd.isBefore(nextStart, "day")) {
      refused.push({ id, message: messages.endBeforeStart })
      return
    }
    patches.push({
      id,
      ...(params.startDate ? { start_date: params.startDate } : {}),
      ...(params.endDate ? { end_date: params.endDate } : {})
    })
  })

  return { patches, refused }
}
